import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Modal,
	Button,
	Input,
	Toggle,
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxList,
	ComboboxItem,
} from '@hydrodigital/design-system';
import MapView, { MapViewHandle } from './MapView';
import StreetView from './StreetView';
import './styles.scss';

const mapboxAccessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

const DEFAULT_LATITUDE = Number(import.meta.env.VITE_MAP_INIT_LATITUDE) || 41.68338;
const DEFAULT_LONGITUDE = Number(import.meta.env.VITE_MAP_INIT_LONGITUDE) || -86.25001;
const GEOCODER_DEBOUNCE_MS = 300;

const LATITUDE_MIN = -90;
const LATITUDE_MAX = 90;
const LONGITUDE_MIN = -180;
const LONGITUDE_MAX = 180;

export type AdjustCoordinatesValue = {
	latitude: number;
	longitude: number;
};

type Props = {
	isOpen: boolean;
	title?: string;
	initialLatitude?: number | null;
	initialLongitude?: number | null;
	onSave: (value: AdjustCoordinatesValue) => void;
	onCancel: () => void;
};

type ViewMode = 'map' | 'street';

type AddressSuggestion = {
	id: string;
	label: string;
	longitude: number;
	latitude: number;
};

function parseNumber(raw: string): number | null {
	if (raw.trim() === '') return null;
	const value = Number(raw);
	return Number.isFinite(value) ? value : null;
}

function validateCoordinate(raw: string, label: string, min: number, max: number): string | null {
	if (raw.trim() === '') return `${label} is required`;
	const value = parseNumber(raw);
	if (value == null) return `Enter a valid ${label.toLowerCase()}`;
	if (value < min || value > max) return `${label} must be between ${min} and ${max}`;
	return null;
}

async function fetchAddressSuggestions(
	query: string,
	signal: AbortSignal,
): Promise<AddressSuggestion[]> {
	if (!mapboxAccessToken || query.trim().length < 3) return [];
	const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
		query,
	)}.json?limit=5&access_token=${mapboxAccessToken}`;
	const response = await fetch(url, { signal });
	if (!response.ok) return [];
	const json = (await response.json()) as {
		features?: Array<{ id: string; place_name: string; center: [number, number] }>;
	};
	return (json.features ?? []).map((feature) => ({
		id: feature.id,
		label: feature.place_name,
		longitude: feature.center[0],
		latitude: feature.center[1],
	}));
}

export default function AdjustCoordinatesModal({
	isOpen,
	title = 'Adjust Coordinates',
	initialLatitude,
	initialLongitude,
	onSave,
	onCancel,
}: Props) {
	const startingLatitude = initialLatitude ?? DEFAULT_LATITUDE;
	const startingLongitude = initialLongitude ?? DEFAULT_LONGITUDE;

	const [latitudeText, setLatitudeText] = useState<string>(String(startingLatitude));
	const [longitudeText, setLongitudeText] = useState<string>(String(startingLongitude));
	const [latitude, setLatitude] = useState<number>(startingLatitude);
	const [longitude, setLongitude] = useState<number>(startingLongitude);
	const [mode, setMode] = useState<ViewMode>('map');
	const [touched, setTouched] = useState({ latitude: false, longitude: false });
	const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
	const mapRef = useRef<MapViewHandle | null>(null);
	const geocodeAbortRef = useRef<AbortController | null>(null);
	const geocodeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!isOpen) return;
		const lat = initialLatitude ?? DEFAULT_LATITUDE;
		const lng = initialLongitude ?? DEFAULT_LONGITUDE;
		setLatitude(lat);
		setLongitude(lng);
		setLatitudeText(String(lat));
		setLongitudeText(String(lng));
		setMode('map');
		setTouched({ latitude: false, longitude: false });
		setAddressSuggestions([]);
	}, [isOpen, initialLatitude, initialLongitude]);

	useEffect(() => {
		return () => {
			geocodeAbortRef.current?.abort();
			if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current);
		};
	}, []);

	const handleMapSelect = useCallback((lng: number, lat: number) => {
		setLatitude(lat);
		setLongitude(lng);
		setLatitudeText(lat.toFixed(6));
		setLongitudeText(lng.toFixed(6));
	}, []);

	const commitTypedCoordinates = useCallback(() => {
		const lat = parseNumber(latitudeText);
		const lng = parseNumber(longitudeText);
		if (lat == null || lng == null) return;
		setLatitude(lat);
		setLongitude(lng);
		mapRef.current?.flyTo(lng, lat);
	}, [latitudeText, longitudeText]);

	const handleAddressSearchChange = useCallback((searchValue: string) => {
		if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current);
		geocodeAbortRef.current?.abort();
		if (searchValue.trim().length < 3) {
			setAddressSuggestions([]);
			return;
		}
		geocodeTimerRef.current = setTimeout(() => {
			const controller = new AbortController();
			geocodeAbortRef.current = controller;
			fetchAddressSuggestions(searchValue, controller.signal)
				.then(setAddressSuggestions)
				.catch(() => undefined);
		}, GEOCODER_DEBOUNCE_MS);
	}, []);

	const handleAddressSelect = useCallback((value: string) => {
		const match = addressSuggestions.find((item) => item.id === value);
		if (!match) return;
		setLatitude(match.latitude);
		setLongitude(match.longitude);
		setLatitudeText(match.latitude.toFixed(6));
		setLongitudeText(match.longitude.toFixed(6));
		mapRef.current?.flyTo(match.longitude, match.latitude);
	}, [addressSuggestions]);

	const handleLocate = useCallback(() => {
		if (!navigator.geolocation) return;
		navigator.geolocation.getCurrentPosition((position) => {
			const lat = position.coords.latitude;
			const lng = position.coords.longitude;
			setLatitude(lat);
			setLongitude(lng);
			setLatitudeText(lat.toFixed(6));
			setLongitudeText(lng.toFixed(6));
			mapRef.current?.flyTo(lng, lat);
		});
	}, []);

	const latitudeError = validateCoordinate(
		latitudeText,
		'Latitude',
		LATITUDE_MIN,
		LATITUDE_MAX,
	);
	const longitudeError = validateCoordinate(
		longitudeText,
		'Longitude',
		LONGITUDE_MIN,
		LONGITUDE_MAX,
	);
	const isValid = !latitudeError && !longitudeError;

	const handleSave = useCallback(() => {
		if (latitudeError || longitudeError) {
			setTouched({ latitude: true, longitude: true });
			return;
		}
		onSave({ latitude: Number(latitudeText), longitude: Number(longitudeText) });
	}, [latitudeError, longitudeError, latitudeText, longitudeText, onSave]);

	const addressComboboxItems = useMemo(
		() =>
			addressSuggestions.map((suggestion) => ({
				value: suggestion.id,
				label: suggestion.label,
			})),
		[addressSuggestions],
	);

	const viewport = useMemo(() => {
		if (mode === 'map') {
			return (
				<div className="adjust-coordinates-viewport__map">
					<MapView
						ref={mapRef}
						latitude={latitude}
						longitude={longitude}
						onSelect={handleMapSelect}
					/>
				</div>
			);
		}
		return <StreetView latitude={latitude} longitude={longitude} />;
	}, [mode, latitude, longitude, handleMapSelect]);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onCancel}
			size="xl"
			closeOnOverlayClick={false}
			closeOnEscape={true}
			showCloseButton={true}
		>
			<Modal.Content>
				<div className="adjust-coordinates-modal">
					<div className="adjust-coordinates-layout">
						<div className="adjust-coordinates-form">
							<h2 className="adjust-coordinates-form__title">{title}</h2>

							<Input
								id="adjust-coordinates-latitude"
								label="Latitude"
								size="small"
								required
								inputMode="decimal"
								value={latitudeText}
								state={touched.latitude && latitudeError ? 'error' : 'default'}
								errorMessage={touched.latitude && latitudeError ? latitudeError : undefined}
								onChange={(e) => setLatitudeText(e.target.value)}
								onBlur={() => {
									setTouched((prev) => ({ ...prev, latitude: true }));
									commitTypedCoordinates();
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter') commitTypedCoordinates();
								}}
							/>

							<Input
								id="adjust-coordinates-longitude"
								label="Longitude"
								size="small"
								required
								inputMode="decimal"
								value={longitudeText}
								state={touched.longitude && longitudeError ? 'error' : 'default'}
								errorMessage={touched.longitude && longitudeError ? longitudeError : undefined}
								onChange={(e) => setLongitudeText(e.target.value)}
								onBlur={() => {
									setTouched((prev) => ({ ...prev, longitude: true }));
									commitTypedCoordinates();
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter') commitTypedCoordinates();
								}}
							/>

							<Combobox
								searchable
								size="small"
								onSearchChange={handleAddressSearchChange}
								onValueChange={handleAddressSelect}
								items={addressComboboxItems}
								filterOption={() => true}
							>
								<ComboboxInput label="Search Address" placeholder="Type an address" />
								<ComboboxContent>
									<ComboboxEmpty>No addresses found.</ComboboxEmpty>
									<ComboboxList>
										{(item) => (
											<ComboboxItem key={item.value} value={item}>
												{item.label}
											</ComboboxItem>
										)}
									</ComboboxList>
								</ComboboxContent>
							</Combobox>

							<div className="adjust-coordinates-form__mode-toggle">
								<Toggle
									checkedText="STREET VIEW"
									uncheckedText="MAP VIEW"
									checked={mode === 'street'}
									onChange={(e) => setMode(e.target.checked ? 'street' : 'map')}
								/>
							</div>
						</div>

						<div className="adjust-coordinates-viewport">
							{viewport}
							{mode === 'map' && (
								<div className="adjust-coordinates-viewport__controls">
									<button type="button" aria-label="Zoom in" onClick={() => mapRef.current?.zoomIn()}>
										+
									</button>
									<button
										type="button"
										aria-label="Zoom out"
										onClick={() => mapRef.current?.zoomOut()}
									>
										−
									</button>
									<button type="button" aria-label="Show your location" onClick={handleLocate}>
										◎
									</button>
								</div>
							)}
						</div>
					</div>
				</div>
			</Modal.Content>
			<Modal.Footer className="flex justify-between px-4">
				<Button label="Cancel" variant="secondary" showIcon={false} onClick={onCancel} />
				<Button
					label="Save"
					variant="primary"
					showIcon={false}
					disabled={!isValid}
					onClick={handleSave}
				/>
			</Modal.Footer>
		</Modal>
	);
}
